export const DIGITAL_TWIN_3D_TAB_STORAGE_KEY = 'petasense_digital_twin_3d_tab_enabled';

/**
 * User toggle for the top-level 3D View tab (Overview Digital Twin card).
 * `null` = user has not chosen; fall back to account preferences.
 */
export function getDigitalTwin3dTabUserPreference() {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(DIGITAL_TWIN_3D_TAB_STORAGE_KEY);
    if (v === 'true') return true;
    if (v === 'false') return false;
  } catch (e) {
    /* ignore quota / private mode */
  }
  return null;
}

/**
 * Persists the Overview toggle and notifies Machine Details to refresh tabs.
 */
export function setDigitalTwin3dTabUserPreference(enabled) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DIGITAL_TWIN_3D_TAB_STORAGE_KEY, enabled ? 'true' : 'false');
    window.dispatchEvent(new CustomEvent('digitalTwin3dTabPreferenceChanged'));
  } catch (e) {
    /* ignore */
  }
}

/**
 * Whether the account has the 3D Digital Twin feature unlocked.
 * Controls the 3D/2D toggle in the Digital Twin widget and the 3D View tab.
 * Enabled per-account via the `digital_twin_3d_enabled` preference flag.
 * Local dev override: set REACT_APP_DIGITAL_TWIN_UI=1 or window.__DIGITAL_TWIN_UI__=true.
 */
export function isDigitalTwinMachineUiEnabled(preferences) {
  // The dunder name is the documented dev-override contract typed into the
  // browser console; renaming it would silently break every existing override.
  // eslint-disable-next-line no-underscore-dangle
  if (typeof window !== 'undefined' && window.__DIGITAL_TWIN_UI__ === true) {
    return true;
  }
  if (process.env.REACT_APP_DIGITAL_TWIN_UI === '1' || process.env.REACT_APP_DIGITAL_TWIN_UI === 'true') {
    return true;
  }
  if (!preferences) return false;
  if (preferences.digital_twin_3d_enabled === true) return true;
  const ef = preferences.enabled_features;
  if (ef && ef.digital_twin_3d === true) return true;
  return false;
}

/**
 * Whether the full Digital Twin machine shell (sidebar + top bar layout) should render.
 * This is a more invasive page-level redesign, intentionally kept separate from the
 * account feature flag so it can be enabled independently via dev overrides only.
 * Use window.__DIGITAL_TWIN_SHELL__=true or REACT_APP_DIGITAL_TWIN_SHELL=1 to enable.
 */
export function isDigitalTwinShellEnabled() {
  // The dunder name is the documented dev-override contract typed into the
  // browser console; renaming it would silently break every existing override.
  // eslint-disable-next-line no-underscore-dangle
  if (typeof window !== 'undefined' && window.__DIGITAL_TWIN_SHELL__ === true) {
    return true;
  }
  if (process.env.REACT_APP_DIGITAL_TWIN_SHELL === '1' || process.env.REACT_APP_DIGITAL_TWIN_SHELL === 'true') {
    return true;
  }
  return false;
}

/**
 * Whether the machine-level **3D View** tab should appear (next to Overview, etc.).
 *
 * The account entitlement is authoritative and is checked first; the Overview card toggle
 * may only ever *hide* an entitled tab, never reveal an unentitled one. It used to be able
 * to: a stored `true` returned early and skipped the account check entirely. Because the
 * preference lives in localStorage it is per-browser, so whichever browser had once
 * switched the toggle on kept the tab forever — on accounts where the backend answers
 * "3D Digital Twin is not enabled for this account". That reads as a Firefox-only bug, but
 * it was only ever "the browser the toggle happened to be on in".
 *
 * A stale stored `true` needs no cleanup, since it is now simply ignored while unentitled.
 * A stored `false` deliberately survives an entitlement round-trip: that is the user's own
 * choice about their own browser, and it should outlast the account flag flipping.
 */
export function shouldShowDigitalTwin3dViewTab(preferences) {
  if (!isDigitalTwinMachineUiEnabled(preferences)) return false;
  return getDigitalTwin3dTabUserPreference() !== false;
}
